// API-Client der /start-Gastfläche.
//
// JSON-Aufrufe (/start, /funnel, /adopt-demo) laufen über `apiRequest` aus
// ../../api/client — das erledigt `credentials`, den Bearer-Header (falls ein
// Token da ist) und den Timeout. Der eigentliche SSE-Stream läuft NICHT hier,
// sondern in useDemoStream.ts über einen eigenen fetch + reader (Muster
// pages/chat/useChatStream.ts).
//
// Endpunkte (Mount: /api/public/demo-chat, server/index.js:728):
//   POST /api/public/demo-chat/start   → DemoOpening
//   POST /api/public/demo-chat/funnel  → fire-and-forget-Ping
//   POST /api/campaigns/adopt-demo     → DemoAdoptionResult (authentifiziert)
//
// Bestehende P13-Signaturen bleiben kompatibel; /start erhält additiv die
// optionale Alters-Selbstauskunft.

import { apiRequest } from '../../api/client';
import type { DemoAgeSelfReport, DemoOpening, DemoTurn } from './types';

/**
 * Startet eine Probeszene. Server: POST /api/public/demo-chat/start
 * {language, age_self_report}. Die Selbstauskunft wird nicht lokal gespeichert.
 * Der Server liefert story_id historisch als Zahl; wir normalisieren auf string
 * (Vertragstyp), damit die Adoption-Payload einen konsistenten string trägt.
 */
export async function startDemo(
  language: string,
  ageSelfReport?: DemoAgeSelfReport,
): Promise<DemoOpening> {
  const raw = await apiRequest<DemoOpening>('/api/public/demo-chat/start', {
    method: 'POST',
    body: {
      language,
      ...(ageSelfReport ? { age_self_report: ageSelfReport } : {}),
    },
  });
  return {
    session_id: String(raw.session_id ?? ''),
    opening_text: String(raw.opening_text ?? ''),
    setting_key: raw.setting_key ?? null,
    story_id: String(raw.story_id ?? ''),
    remaining: Number(raw.remaining ?? 0),
  };
}

/**
 * Trichter-Ping (fire-and-forget). Server: POST /api/public/demo-chat/funnel
 * {session_id, stage}. Fehler werden geschluckt — ein fehlgeschlagener Ping
 * darf NIE den Ablauf (Eröffnung/Registrierung/Adoption) blockieren.
 */
export async function pingFunnel(
  sessionId: string,
  stage: 'read_opening' | 'reg_started' | 'adopted',
): Promise<void> {
  try {
    await apiRequest<void>('/api/public/demo-chat/funnel', {
      method: 'POST',
      body: { session_id: sessionId, stage },
    });
  } catch {
    // Trichter-Ping ist unkritisch — still schlucken.
  }
}

export interface DemoAdoptionPayload {
  session_id: string;
  language: string;
  setting_key: string | null;
  story_id: string;
  opening_text: string;
  turns: DemoTurn[];
}

export interface DemoAdoptionResult {
  campaign_id: number;
  show_onboarding_tour: boolean;
}

/**
 * Adoptiert die Probeszene in eine echte Kampagne. Authentifiziert — der
 * Bearer-Token wird von apiRequest gesetzt; auf Web trägt die Cookie-Session.
 * Server: POST /api/campaigns/adopt-demo.
 */
export async function adoptDemo(payload: DemoAdoptionPayload): Promise<DemoAdoptionResult> {
  return apiRequest<DemoAdoptionResult>('/api/campaigns/adopt-demo', {
    method: 'POST',
    body: payload,
  });
}
